import { useAmplifyREST } from "hooks/useAmplifyREST";
import { Divider, Page, Spinner } from "@whitespace/whitespace-ui";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card } from "react-bootstrap";
import { IMapMarker } from "interfaces/components/Map/IMapConfig";
import IMapLatLng from "interfaces/components/Map/IMapLatLng";
import { useMapControl } from "hooks/useMapControl";
import IGetActiveVehiclesTrackingResponseModel from "interfaces/Response/IGetActiveVehiclesTrackingResponseModel";
import IActiveSiteModel from "interfaces/IActiveSiteModel";
import { IHwrcSiteMapPanelProps } from "interfaces/components/Map/IHwrcSiteMapPanelProps";
import { IDisposalSiteMapPanelProps } from "interfaces/components/Map/IDisposalSiteMapPanelProps";
import { IVehicleMapPanelProps } from "interfaces/components/Map/IVehicleMapPanelProps";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { mapDisposalSiteModelToViewModels } from "domains/Settings/DisposalSites/DataMappers/ManageDisposalSiteMapper";
import IDisposalSiteViewModel from "interfaces/IDisposalSiteViewModel";
import { SourceEnum } from "modules/sourceEnum";
import {
  mapHwrcActiveSitesToMapMarkers,
  mapActiveVehiclesToMapMarkers,
  mapDisposalActiveSitesToMapMarkers,
  getDetailsById
} from "./DataMappers/MapDataMapper";
import GoogleMap from "./GoogleMap";
import MapKeys from "./MapKeys";
import VehicleMapPanel from "./VehicleMapPanel";
import DisposalSiteMapPanel from "./DisposalSiteMapPanel";
import HwrcSiteMapPanel from "./HwrcSiteMapPanel";

const Map = () => {
  const { sourceType, sourceId } = useParams();
  const [mapMarkersLoaded, setMapMarkersLoaded] = useState<boolean>(false);
  const [mapMarkers, setMapMarkers] = useState<IMapMarker[]>([]);
  const [sitesLoaded, setSitesLoaded] = useState<boolean>(false);
  const [vehiclesLoaded, setVehiclesLoaded] = useState<boolean>(false);
  const [selectedVehicle, setSelectedVehicle] = useState<IVehicleMapPanelProps | null>(null);
  const [selectedHwrcSite, setSelectedHwrcSite] = useState<IHwrcSiteMapPanelProps | null>(null);
  const [selectedDisposalSite, setSelectedDisposalSite] = useState<IDisposalSiteMapPanelProps | null>(null);
  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [activeHwrcSitesMapMarkers, setActiveHwrcSitesMapMarkers] = useState<
  IMapMarker[]
  >([]);
  const [activeDisposalSitesMapMarkers, setActiveDisposalSitesMapMarkers] = useState<IMapMarker[]>([]);
  const [activeVehiclesMapMarkers, setActiveVehiclesMapMarkers] = useState<
  IMapMarker[]
  >([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [zoom, setZoom] = useState(14);
  const [toggleTraffic, setToggleTraffic] = useState<string[] | undefined>([]);
  const [currentMap, setCurrentMap] = useState<any>(null);
  const [mapPositionRight, setMapPositionRight] = useState(null);
  const [mapPositionLeft, setMapPositionLeft] = useState(null);
  const [center] = useState<IMapLatLng>({
    latitude: 1.1,
    longitude: -1.1
  });
  const [isSending, setIsSending] = useState<boolean>(true);
  const [mapBounds, setMapBounds] = useState<any>(null);
  const [apiErrors, setApiErrors] = useState<string[]>([]);
  const [disposalSites, setDisposalSites] = useState<
  IDisposalSiteViewModel[] | undefined
  >(undefined);

  const {
    isSending: getActiveSitesIsSending,
    errors: getActiveSitesErrors,
    data: getActiveSitesData,
    sendRequest: getActiveSendRequest
  } = useAmplifyREST();

  const {
    isSending: getVehiclesIsSending,
    errors: getVehiclesErrors,
    data: getVehiclesData,
    sendRequest: getVehiclesSendRequest
  } = useAmplifyREST();

  const {
    isSending: disposalSitesIsSending,
    errors: disposalSitesErrors,
    data: disposalSitesData,
    sendRequest: disposalSiteSendRequest
  } = useAmplifyREST();

  useEffect(() => {
    getActiveSendRequest("map/sites/active", "GET");
  }, [getActiveSendRequest]);

  useEffect(() => {
    getVehiclesSendRequest("map/vehicles/active", "GET");
  }, [getVehiclesSendRequest]);

  useEffect(() => {
    disposalSiteSendRequest("settings/disposalsites", "GET");
  }, [disposalSiteSendRequest]);

  useEffect(() => {
    setIsSending(
      getVehiclesIsSending || getActiveSitesIsSending || disposalSitesIsSending
    );
  }, [
    setIsSending,
    getVehiclesIsSending,
    getActiveSitesIsSending,
    disposalSitesIsSending
  ]);

  useEffect(() => {
    if (getVehiclesErrors) {
      setApiErrors(result => [...result, ...getVehiclesErrors]);
      setShowExceptions(true);
      setVehiclesLoaded(true);
    }
  }, [getVehiclesErrors, setShowExceptions, setApiErrors]);

  useEffect(() => {
    if (getActiveSitesErrors) {
      setApiErrors(result => [...result, ...getActiveSitesErrors]);
      setShowExceptions(true);
      setSitesLoaded(true);
    }
  }, [getActiveSitesErrors, setShowExceptions, setApiErrors]);

  useEffect(() => {
    if (disposalSitesErrors) {
      setApiErrors(result => [...result, ...disposalSitesErrors]);
      setShowExceptions(true);
      setSitesLoaded(true);
    }
  }, [disposalSitesErrors, setShowExceptions, setApiErrors]);

  const onVehicleSidePanelClosed = () => {
    setSelectedVehicle(null);
  };

  const onHwrcSiteSidePanelClosed = () => {
    setSelectedHwrcSite(null);
  };

  const onDisposalSiteSidePanelClosed = () => {
    setSelectedDisposalSite(null);
  };

  useEffect(() => {
    if (disposalSitesData && disposalSitesData.disposalSites) {
      setDisposalSites(
        mapDisposalSiteModelToViewModels(disposalSitesData.disposalSites)
      );
    }
  }, [disposalSitesData, setDisposalSites]);

  const isSendingHandler = useCallback(
    (sending: boolean) => {
      setIsSending(sending);
    },
    [setIsSending]
  );

  const addToExceptionsHandler = useCallback(
    (exception: any) => {
      setApiErrors(result => [...result, ...exception]);
      setShowExceptions(true);
    },
    [setIsSending]
  );

  const vehiclePinClicked = (id: number | undefined, details: any) => {
    if (id) {
      const vehicleMapPanel: IVehicleMapPanelProps = {
        vehicleDetails: details,
        onClose: onVehicleSidePanelClosed,
        id: id!,
        isSendingHandler,
        addToExceptionsHandler
      };
      setSelectedVehicle(vehicleMapPanel);
      setSelectedDisposalSite(null);
      setSelectedHwrcSite(null);
    }
  };

  const hwrcSitePinClicked = (id: number | undefined, details: any) => {
    if (id) {
      const hwrcMapPanel: IHwrcSiteMapPanelProps = {
        title: details,
        id: id!,
        onClose: onHwrcSiteSidePanelClosed,
        isSendingHandler,
        addToExceptionsHandler
      };
      setSelectedHwrcSite(hwrcMapPanel);

      setSelectedVehicle(null);
      setSelectedDisposalSite(null);
    }
  };

  const disposalSitePinClicked = (id: number | undefined, details: any) => {
    if (id) {
      const disposalSiteMapPanel: IDisposalSiteMapPanelProps = {
        title: details,
        id: id!,
        onClose: onDisposalSiteSidePanelClosed,
        disposalSites
      };
      setSelectedDisposalSite(disposalSiteMapPanel);

      setSelectedHwrcSite(null);
      setSelectedVehicle(null);
    }
  };

  useEffect(() => {
    if (getActiveSitesData) {
      const hwrcSiteMarkers = mapHwrcActiveSitesToMapMarkers(
        getActiveSitesData.hwrcSites as IActiveSiteModel[],
        hwrcSitePinClicked
      );

      const disposalSiteMarkers = mapDisposalActiveSitesToMapMarkers(
        getActiveSitesData.disposalSites as IActiveSiteModel[],
        disposalSitePinClicked
      );

      setActiveHwrcSitesMapMarkers(hwrcSiteMarkers);
      setActiveDisposalSitesMapMarkers(disposalSiteMarkers);
      setSitesLoaded(true);
    }
  }, [
    getActiveSitesData,
    setActiveHwrcSitesMapMarkers,
    setActiveDisposalSitesMapMarkers,
    setSitesLoaded
  ]);

  useEffect(() => {
    if (getVehiclesData) {
      const vehicleMarkers = mapActiveVehiclesToMapMarkers(
        getVehiclesData as IGetActiveVehiclesTrackingResponseModel,
        vehiclePinClicked
      );
      setActiveVehiclesMapMarkers(vehicleMarkers);
      setVehiclesLoaded(true);
    }
  }, [getVehiclesData, setActiveHwrcSitesMapMarkers, setVehiclesLoaded]);

  useEffect(() => {
    if (sitesLoaded && vehiclesLoaded) {
      setMapMarkers([
        ...activeHwrcSitesMapMarkers,
        ...activeDisposalSitesMapMarkers,
        ...activeVehiclesMapMarkers
      ]);
      setMapMarkersLoaded(true);
    }
  }, [sitesLoaded, vehiclesLoaded, setMapMarkersLoaded]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
    setApiErrors([]);
  }, [setShowExceptions]);

  const handleTrafficToggle = useCallback(() => {
    if (toggleTraffic === undefined || toggleTraffic.length === 0) {
      setToggleTraffic(["TrafficLayer"]);
    } else setToggleTraffic(undefined);
  }, [toggleTraffic, setToggleTraffic]);

  const fitMapBounds = () => {
    if (currentMap && mapBounds && mapLoaded) {
      currentMap!.map.fitBounds(mapBounds);
      currentMap!.map.panToBounds(mapBounds);

      const zoomFactor = mapBounds.length === 1 ? 14 : currentMap.map.zoom;
      setZoom(zoomFactor);
    }
  };

  useEffect(() => {
    if (currentMap && mapBounds && mapLoaded) {
      fitMapBounds();
    }
  }, [mapLoaded, mapBounds, currentMap]);

  const onMapLoadHandler = (googleMap: any, bounds: any) => {
    setMapLoaded(true);
    setCurrentMap(googleMap);
    setMapBounds(bounds);
    setMapPositionRight(googleMap.maps.ControlPosition.RIGHT_BOTTOM);
    setMapPositionLeft(googleMap.maps.ControlPosition.LEFT_BOTTOM);
  };

  const handleRecenterMap = useCallback(() => {
    fitMapBounds();
  }, [fitMapBounds]);

  const handleZoomIn = useCallback(() => {
    if (zoom < 22) {
      setZoom(zoom + 1);
    }
  }, [zoom]);

  const handleZoomOut = useCallback(() => {
    if (zoom > 4) {
      setZoom(zoom - 1);
    }
  }, [zoom]);

  useMapControl(
    currentMap,
    mapPositionLeft,
    <MapKeys
      position={mapPositionLeft}
      handleRecenterMap={handleRecenterMap}
      handleZoomIn={handleZoomIn}
      handleZoomOut={handleZoomOut}
      handleTrafficToggle={handleTrafficToggle}
    />,
    ""
  );

  useMapControl(
    currentMap,
    mapPositionRight,
    <MapKeys
      position={mapPositionRight}
      handleRecenterMap={handleRecenterMap}
      handleZoomIn={handleZoomIn}
      handleZoomOut={handleZoomOut}
      handleTrafficToggle={handleTrafficToggle}
    />,
    ""
  );

  useEffect(() => {
    if (
      mapMarkersLoaded
      && sourceId !== undefined
      && sourceType !== undefined
    ) {
      switch (sourceType) {
        case SourceEnum.Vehicle: {
          const vehicleDetails = getDetailsById(
            sourceId,
            activeVehiclesMapMarkers!
          );
          if (vehicleDetails) {
            vehiclePinClicked(vehicleDetails.key, vehicleDetails.details);
          }
          break;
        }
        case SourceEnum.DisposalSite: {
          const disposalSiteDetails = getDetailsById(
            sourceId,
            activeDisposalSitesMapMarkers!
          );
          if (disposalSiteDetails) {
            disposalSitePinClicked(
              disposalSiteDetails.key,
              disposalSiteDetails.title
            );
          }
          break;
        }

        case SourceEnum.HwrcSite: {
          const hwrcSiteDetails = getDetailsById(
            sourceId,
            activeHwrcSitesMapMarkers!
          );
          if (hwrcSiteDetails) {
            hwrcSitePinClicked(hwrcSiteDetails.key, hwrcSiteDetails.title);
          }
          break;
        }
        default:
          break;
      }
    }
  }, [
    sourceId,
    sourceType,
    activeVehiclesMapMarkers,
    activeVehiclesMapMarkers,
    mapMarkersLoaded
  ]);

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="map-title">
              Map
              <Spinner
                animation="grow"
                visible={isSending}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
        </Card.Header>
        <Card.Body>
          {mapMarkersLoaded && mapMarkers && (
            <div style={{ position: "relative" }}>
              <GoogleMap
                markers={mapMarkers}
                googleMapOptions={_maps => ({
                  zoomControl: false,
                  fullscreenControl: false
                })}
                zoom={zoom}
                onMapLoad={onMapLoadHandler}
                layerTypes={toggleTraffic}
                center={center}
              />
              {selectedVehicle && (
                <VehicleMapPanel
                  vehicleDetails={selectedVehicle.vehicleDetails}
                  onClose={selectedVehicle.onClose}
                  id={selectedVehicle.id}
                  isSendingHandler={isSendingHandler}
                  addToExceptionsHandler={addToExceptionsHandler}
                />
              )}

              {selectedDisposalSite && disposalSites && (
                <DisposalSiteMapPanel
                  onClose={selectedDisposalSite.onClose}
                  id={selectedDisposalSite.id}
                  title={selectedDisposalSite.title}
                  disposalSites={disposalSites}
                />
              )}

              {selectedHwrcSite && (
                <HwrcSiteMapPanel
                  onClose={selectedHwrcSite.onClose}
                  id={selectedHwrcSite.id}
                  title={selectedHwrcSite.title}
                  isSendingHandler={isSendingHandler}
                  addToExceptionsHandler={addToExceptionsHandler}
                />
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    </Page>
  );
};

export default Map;
